import { call, put, takeLatest, all } from 'redux-saga/effects';
import {
    IFetchEmailTemplatesPayload,
    ISaveEmailTemplatePayload,
    IDeleteEmailTemplatePayload,
    ISendEmailPayload,
    types,
} from './types';
import {
    fetchEmailTemplatesSuccess,
    fetchEmailTemplatesFail,
    saveEmailTemplateSuccess,
    saveEmailTemplateFail,
    deleteEmailTemplateSuccess,
    deleteEmailTemplateFail,
    sendEmailSuccess,
    sendEmailFail,
    fetchProductEmailCategories,
    fetchProductEmailCategoriesSuccess,
    fetchProductEmailCategoriesFail,
} from './action';
import { api } from '../../configurations/api';
import {
    emailTemplatesUrl,
    productEmailCategoriesUrl,
    sendEmailUrl,
    sendProductEmailUrl,
} from '../../configurations/api/url';
import { CUSTOM_TEMPLATE_CATEGORY } from '../../utils/productEmailCatalog';
import { unwrapApiPayload } from '../../utils/campaignHostSubscriptionApi';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { Action } from 'redux';
import { globalErrorHandler } from '../error/saga';
import { showToastMessage } from '../../utils/AppUtils';
import type { IProductEmailCategory } from './types';

function* fetchEmailTemplatesSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IFetchEmailTemplatesPayload;
    resolve?: (data: any) => void;
    reject?: (error: any) => void;
}): any {
    try {
        const queryParams = new URLSearchParams();
        if (payload.page) queryParams.append('page', String(payload.page));
        if (payload.limit) queryParams.append('limit', String(payload.limit));
        if (payload.searchTerm) queryParams.append('searchTerm', payload.searchTerm);

        const url = `${emailTemplatesUrl}?${queryParams.toString()}`;
        const response = yield call(api, url, httpRequest.GET, null, 0, 0);
        yield put(fetchEmailTemplatesSuccess(response));
        if (resolve) {
            resolve(response);
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchEmailTemplatesFail(error));
        yield call(globalErrorHandler, error);
        if (reject) {
            reject(error);
        }
    }
}

function* saveEmailTemplateSaga({ payload }: { payload: ISaveEmailTemplatePayload }): any {
    try {
        if (payload._id) {
            // Update existing template
            const response = yield call(api, `${emailTemplatesUrl}/${payload._id}`, httpRequest.PATCH, payload, 0, 0);
            const { data } = response.data;
            yield put(saveEmailTemplateSuccess(data));
            yield call(showToastMessage, 'Email template updated successfully', 'success');
        } else {
            // Create new template
            const response = yield call(api, emailTemplatesUrl, httpRequest.POST, payload, 0, 0);
            const { data } = response.data;
            yield put(saveEmailTemplateSuccess(data));
            yield call(showToastMessage, 'Email template created successfully', 'success');
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveEmailTemplateFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteEmailTemplateSaga({ payload }: { payload: IDeleteEmailTemplatePayload }): any {
    try {
        yield call(api, `${emailTemplatesUrl}/${payload.id}`, httpRequest.DELETE, {}, 0, 0);
        yield put(deleteEmailTemplateSuccess(payload.id));
        yield call(showToastMessage, 'Email template deleted successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteEmailTemplateFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* sendEmailSaga({ payload }: { payload: ISendEmailPayload }): any {
    try {
        const isProduct = Boolean(payload.categoryId) && payload.categoryId !== 'custom-template';
        const url = isProduct ? sendProductEmailUrl : sendEmailUrl;
        const body = isProduct
            ? {
                  categoryId: payload.categoryId,
                  entityId: payload.entityId,
                  audienceScope: payload.audienceScope,
                  userIds: payload.userIds,
                  audience: payload.audience,
                  subject: payload.subject,
                  message: payload.message,
                  image: payload.image,
                  note: payload.note,
                  doNotRepeat: payload.doNotRepeat,
                  largeHeroImage: payload.largeHeroImage,
              }
            : payload;
        const response = yield call(api, url, httpRequest.POST, body, 0, 0);
        yield put(sendEmailSuccess(response));
        yield call(showToastMessage, 'Email sent successfully', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(sendEmailFail(error));
        const missingVideo = error?.data?.details?.missingVideoSlots || error?.data?.data?.missingVideoSlots;
        const missingImage = error?.data?.details?.missingImageSlots || error?.data?.data?.missingImageSlots;
        const missingText = error?.data?.details?.missingTextSlots || error?.data?.data?.missingTextSlots;
        if (Array.isArray(missingVideo) && missingVideo.length) {
            yield call(
                showToastMessage,
                error?.data?.message || `Missing video slots: ${missingVideo.join(', ')}`,
                'error',
            );
        } else if (Array.isArray(missingImage) && missingImage.length) {
            yield call(
                showToastMessage,
                error?.data?.message || `Missing image slots: ${missingImage.join(', ')}`,
                'error',
            );
        } else if (Array.isArray(missingText) && missingText.length) {
            yield call(
                showToastMessage,
                error?.data?.message || `Missing text slots: ${missingText.join(', ')}`,
                'error',
            );
        } else {
            yield call(globalErrorHandler, error);
        }
    }
}

interface FetchEmailTemplatesAction extends Action {
    payload: IFetchEmailTemplatesPayload;
    resolve?: (data: any) => void;
    reject?: (error: any) => void;
}

interface SaveEmailTemplateAction extends Action {
    payload: ISaveEmailTemplatePayload;
}

interface DeleteEmailTemplateAction extends Action {
    payload: IDeleteEmailTemplatePayload;
}

interface SendEmailAction extends Action {
    payload: ISendEmailPayload;
}

function* fetchProductEmailCategoriesSaga({
    resolve,
    reject,
}: {
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, productEmailCategoriesUrl, httpRequest.GET, null, 2);
        const data = unwrapApiPayload<{ categories?: IProductEmailCategory[] }>(response);
        const categories = data?.categories || [];
        const result = [CUSTOM_TEMPLATE_CATEGORY, ...categories.filter(c => c.id !== 'custom-template')];
        yield put(fetchProductEmailCategoriesSuccess(result));
        if (typeof resolve === 'function') resolve(result);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchProductEmailCategoriesFail(error));
        if (typeof reject === 'function') reject(error);
    }
}

export default function* emailTemplatesSaga() {
    yield all([
        takeLatest<FetchEmailTemplatesAction>(types.FETCH_EMAIL_TEMPLATES, fetchEmailTemplatesSaga),
        takeLatest<SaveEmailTemplateAction>(types.SAVE_EMAIL_TEMPLATE, saveEmailTemplateSaga),
        takeLatest<DeleteEmailTemplateAction>(types.DELETE_EMAIL_TEMPLATE, deleteEmailTemplateSaga),
        takeLatest<SendEmailAction>(types.SEND_EMAIL, sendEmailSaga),
        takeLatest(fetchProductEmailCategories as any, fetchProductEmailCategoriesSaga),
    ]);
}
